import React from 'react';
import { Box, Button, CircularProgress } from '@mui/material';
import { AutocompleteField } from '~/components/AutocompleteField/AutocompleteField.tsx';
import type { TAutocompleteOption } from '~/components/AutocompleteField/types.ts';
import { guaranteesClientPageStyles } from '~/modules/guarantees/form/client/styles.ts';
import ButtonLoadingIcon from '~/modules/guarantees/form/shared/ButtonLoadingIcon.tsx';
import type {
  TGuaranteesMainDealConditions,
  TPatchGuaranteesMainDealConditionsPayload,
} from '~/store/guarantees/types.ts';
import FlowSection from '../../components/root/shared/FlowSection.tsx';
import {
  MANAGER_APPROVAL_LETTER_LOADING_TEXT,
  MANAGER_APPROVAL_LETTER_TEXT,
} from './constants.ts';
import ManagerMainDealConditionsSection from './ManagerMainDealConditionsSection.tsx';

type TManagerApprovalLetterDocumentSectionProps = {
  coverageTypeOption: TAutocompleteOption | null;
  coverageTypeOptions: TAutocompleteOption[];
  isDocumentLoading: boolean;
  isMainDealConditionsLoading: boolean;
  isMainDealConditionsSaving: boolean;
  isSubmitDisabled: boolean;
  isSubmitting: boolean;
  mainDealConditions: TGuaranteesMainDealConditions | null;
  pdfUrl: string;
  showCoverageTypeField: boolean;
  onCoverageTypeChange: (value: TAutocompleteOption | null) => void;
  onMainDealConditionsDirtyChange: (isDirty: boolean) => void;
  onMainDealConditionsSubmit: (
    payload: TPatchGuaranteesMainDealConditionsPayload
  ) => Promise<boolean>;
  onOpenPdf: () => void;
  onSubmit: () => void;
};

const ManagerApprovalLetterDocumentSection: React.FC<
  TManagerApprovalLetterDocumentSectionProps
> = ({
  coverageTypeOption,
  coverageTypeOptions,
  isDocumentLoading,
  isMainDealConditionsLoading,
  isMainDealConditionsSaving,
  isSubmitDisabled,
  isSubmitting,
  mainDealConditions,
  pdfUrl,
  showCoverageTypeField,
  onCoverageTypeChange,
  onMainDealConditionsDirtyChange,
  onMainDealConditionsSubmit,
  onOpenPdf,
  onSubmit,
}) => (
  <FlowSection
    step={MANAGER_APPROVAL_LETTER_TEXT.sectionStep}
    title={MANAGER_APPROVAL_LETTER_TEXT.sectionTitle}
    description={MANAGER_APPROVAL_LETTER_TEXT.sectionDescription}
  >
    <ManagerMainDealConditionsSection
      mainDealConditions={mainDealConditions}
      isLoading={isMainDealConditionsLoading}
      isSaving={isMainDealConditionsSaving}
      onDirtyChange={onMainDealConditionsDirtyChange}
      onSubmit={onMainDealConditionsSubmit}
    />

    <Box
      sx={[
        guaranteesClientPageStyles.generatedDocumentPreview,
        (theme) => ({ mt: theme.spacing(2) }),
      ]}
    >
      {isDocumentLoading ? (
        <Box sx={guaranteesClientPageStyles.flowLoadingState}>
          <CircularProgress size={24} />
          {MANAGER_APPROVAL_LETTER_LOADING_TEXT}
        </Box>
      ) : (
        <Box
          component="iframe"
          src={pdfUrl || undefined}
          title={MANAGER_APPROVAL_LETTER_TEXT.previewTitle}
          sx={guaranteesClientPageStyles.generatedDocumentIframe}
        />
      )}

      <Box sx={guaranteesClientPageStyles.generatedDocumentActions}>
        {showCoverageTypeField ? (
          <AutocompleteField
            label={MANAGER_APPROVAL_LETTER_TEXT.coverageTypeLabel}
            options={coverageTypeOptions}
            placeholder={MANAGER_APPROVAL_LETTER_TEXT.coverageTypePlaceholder}
            value={coverageTypeOption}
            disabled={isSubmitting}
            sx={{ minWidth: { xs: '100%', sm: 320 } }}
            onChange={onCoverageTypeChange}
          />
        ) : null}

        <Button
          variant="outlined"
          color="primary"
          type="button"
          sx={guaranteesClientPageStyles.generatedDocumentOpenButton}
          disabled={!pdfUrl}
          onClick={onOpenPdf}
        >
          {MANAGER_APPROVAL_LETTER_TEXT.openButton}
        </Button>
        <Button
          variant="contained"
          color="primary"
          type="button"
          sx={guaranteesClientPageStyles.generatedDocumentOpenButton}
          disabled={isSubmitDisabled}
          onClick={onSubmit}
          startIcon={isSubmitting ? <ButtonLoadingIcon /> : undefined}
        >
          {MANAGER_APPROVAL_LETTER_TEXT.submitButton}
        </Button>
      </Box>
    </Box>
  </FlowSection>
);

export default ManagerApprovalLetterDocumentSection;
